import React, {useEffect, useReducer, useRef, useState} from 'react';
import {Platform, StyleProp, StyleSheet, Text, TextInput, View, ViewStyle} from 'react-native';

import {fieldBoxChromeStyle, fieldChrome} from '../../../design-system/form/FormField';
import {fieldPalette} from '../../../design-system/tokens/fieldPalette';
import {otp, radius, space} from '../../../design-system/tokens/spacing';
import {otpDigit} from '../../../design-system/tokens/typography';
import rewardsTheme from '../../../theme/rewardsTheme';
import {logger} from '../../../utils/logger';
import {fontFamilyStyle} from '../Text';

type State = {cursor: number; values: string[]};

type Action =
  | {type: 'reset'}
  | {type: 'insertText'; payload: {value: string; index: number}}
  | {type: 'insertFromPaste'; payload: {value: string}}
  | {type: 'sms'; payload: {value: string}}
  | {type: 'deleteContentBackward'; payload: {index: number}};

function reducer(state: State, action: Action) {
  if (action.type === 'reset') {
    return {cursor: 0, values: Array(state.values.length).fill('')};
  }

  // Create new values array to avoid mutation
  const newValues = [...state.values];
  let newCursor = state.cursor;

  if (action.type === 'insertText') {
    newValues[action.payload.index] = action.payload.value;
  } else if (action.type === 'insertFromPaste') {
    for (let i = 0; i < newValues.length; i++) {
      newValues[i] = action.payload.value[i] || '';
    }
  } else if (action.type === 'deleteContentBackward') {
    newValues[action.payload.index] = '';
  }
  // code from sms safari ios is sent as several
  // onChange event nativeEvent.type = 'input'
  // chrome ios sms sends as 1 value so can use the simpler insertFromPaste
  else if (action.type === 'sms') {
    // value was sometimes 2 char. just the last char should be used.
    newValues[state.cursor] = action.payload.value[action.payload.value.length - 1];
    if (state.cursor === newValues.length - 1) {
      newCursor = 0;
    } else {
      newCursor = state.cursor + 1;
    }
  }

  return {cursor: newCursor, values: newValues};
}

// Both handlers are passed straight through to the TextInput below, so take
// their types from it rather than restating them.
type BoxProps = React.ComponentProps<typeof TextInput>;

export type OtpProps = {
  numInputs: number;
  onChangeText?: (text: string) => void;
  containerStyle?: StyleProp<ViewStyle>;
  onEnter?: () => void;
  onTouchStart?: BoxProps['onTouchStart'];
  onBlur?: BoxProps['onBlur'];

  /**
   * Should apply error styles
   */
  error?: boolean;

  /**
   * This is just used to reset the field when set to empty string
   */
  value?: string;

  /**
   * Opt-in fixed box geometry (DIS-1844 desktop card, `otp` token). Each box
   * takes this width and height instead of growing to fill the row, the row is
   * centred, and the per-box error-line spacer is dropped so the owner spaces
   * the row with layout tokens. Absent: the flex-grown boxes, unchanged.
   */
  boxSize?: {width: number; height: number};
};

// DIS-1750: Otp was built on themed/TextInput, which wraps @rneui's Input and
// takes its input styling from rewardsTheme's `TextInput` entry. All three
// consumers render it inside <ThemeProvider theme={rewardsTheme}>. The styles
// below restate what that stack resolved to, so the boxes look the same on
// React Native's TextInput. Otp.test.tsx pins the resolved values.
//
// Colours are read from rewardsTheme, the same source the stack read them from.
const TEXT_COLOR = rewardsTheme.lightColors?.black;
const ERROR_COLOR = rewardsTheme.lightColors?.error;
const MUTED_COLOR = rewardsTheme.lightColors?.grey3;

// SHORTCUT: spacing and geometry literals - containerStyle's gap (below),
// box's borderWidth, and textInputStyle's minHeight,
// paddingHorizontal, paddingVertical and width. Ceiling: this file's OTP boxes only. Upgrade path: spacing
// tokens under app/design-system/tokens/ - DIS-1737 blocks them, DIS-1735 is
// the epic.
const styles = StyleSheet.create({
  containerStyle: {
    flexDirection: 'row',
    gap: 8,
  },
  // boxSize only: fixed boxes, so the row is centred in its column, spaced by
  // the `otp` token (536:4581).
  containerSized: {
    justifyContent: 'center',
    gap: otp.gap,
  },
  // The box around each input carries the only border, with FormField's
  // geometry (1px on all four sides, radius 4). DIS-1824: the box used to
  // have no bottom edge and the TextInput drew a second, rounder border
  // inside it. The per-state colours and border width come from fieldChrome
  // through fieldBoxChromeStyle; borderWidth here is the 1px base.
  box: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: radius.control,
  },
  textInputStyle: {
    ...otpDigit,
    color: TEXT_COLOR,
    flex: 1,
    minHeight: 40,
    paddingHorizontal: space.s8,
    paddingVertical: 12,
    textAlign: 'center',
    width: 44,
    ...Platform.select({
      web: {
        outlineWidth: 0,
        outlineStyle: 'none',
      },
    }),
  },
  textInputStyleError: {
    color: ERROR_COLOR,
  },
  // SHORTCUT: @rneui Input always rendered a one-space error line under each
  // box. Otp never shows an error message there, but the line's height is
  // part of the layout, so it is kept. Ceiling: layout parity only. Upgrade
  // path: drop it together with a design sign-off on the new spacing.
  errorLineSpacer: {
    ...fontFamilyStyle,
    margin: space.s4,
    fontSize: 12,
    color: ERROR_COLOR,
  },
});

/**
 * Otp for rewards program. evaluated other but they were a bit buggy or didn't have the behaviour we required
 * react-native-otp-textinput react-otp-input
 * @param param0
 * @returns
 */
export default function Otp({
  numInputs = 4,
  onChangeText,
  containerStyle,
  onEnter,
  onTouchStart,
  onBlur,
  error,
  value,
  boxSize,
}: OtpProps) {
  const otpInput = useRef(Array(numInputs).fill({}));
  // True only while the reset effect below runs for the first time, whatever
  // `value` was then. See the comment inside that effect.
  const isMountPassRef = useRef(true);
  // Box that holds focus. Drawn as the `focus` state only when the brand
  // palette asks for it (fieldPalette.otpFocus, Thirsty Camel since DIS-1825).
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const [state, dispatch] = useReducer(reducer, {
    // cursor used to keep track of sms input
    cursor: 0,
    values: Array(numInputs).fill(''),
  });

  // Call onChangeText when values change
  const valuesString = state.values.join('');
  useEffect(() => {
    onChangeText?.(valuesString);
  }, [valuesString, onChangeText]);

  // This is a bit of a hack to reset the field
  // if value prop is set to empty string
  useEffect(() => {
    if (value === '') {
      otpInput.current?.forEach(e => {
        e.clear();
      });
      dispatch({type: 'reset'});
      // DIS-1673: an external clear means "start over", so put the caret back
      // in the first box. Without this the caret stays where it was — after a
      // full code that is the LAST box, and the insertText branch below only
      // advances focus while `i < numInputs - 1`, so every retyped digit
      // overwrites that one box and the joined value never reaches numInputs
      // again. Every consumer gates VERIFY on the joined length (DIS-1824), so
      // that is a dead end.
      //
      // Skipped on the mount pass: `value` starts as '' on all three
      // consumers, and focusing there would raise the keyboard on mount — a
      // behaviour change none of them asked for.
      if (!isMountPassRef.current) {
        otpInput.current?.[0]?.focus?.();
      }
    }
    isMountPassRef.current = false;
  }, [value]);

  return (
    <View
      style={
        boxSize
          ? [styles.containerStyle, styles.containerSized, containerStyle]
          : [styles.containerStyle, containerStyle]
      }>
      {state.values.map((val, i) => {
        const showFocus = fieldPalette.otpFocus && focusedIndex === i;
        const chrome = fieldChrome(showFocus ? 'focus' : val.length > 0 ? 'filled' : 'default');

        return (
          <View key={i} style={boxSize ? {width: boxSize.width} : {flexGrow: 1, flexShrink: 1}}>
            <View
              style={
                boxSize
                  ? [styles.box, fieldBoxChromeStyle(chrome), {height: boxSize.height}]
                  : [styles.box, fieldBoxChromeStyle(chrome)]
              }>
              <TextInput
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="numeric"
                returnKeyLabel="next"
                returnKeyType="next"
                selectTextOnFocus
                underlineColorAndroid="transparent"
                placeholderTextColor={MUTED_COLOR}
                ref={e => {
                  otpInput.current[i] = e;
                }}
                value={val}
                placeholder={'•'}
                onTouchStart={e => {
                  onTouchStart?.(e);
                }}
                onFocus={() => {
                  setFocusedIndex(i);
                }}
                onBlur={e => {
                  setFocusedIndex(current => (current === i ? null : current));
                  onBlur?.(e);
                }}
                onChange={e => {
                  logger.debug('Otp', 'Input change event', {inputType: e.nativeEvent.inputType});
                  e.preventDefault();
                  if (e.nativeEvent.inputType === 'deleteContentBackward') {
                    dispatch({
                      type: 'deleteContentBackward',
                      payload: {index: i},
                    });
                    if (i > 0) {
                      otpInput.current[i - 1].focus();
                    }
                  } else if (
                    // web
                    e.nativeEvent.inputType === 'insertText' ||
                    // native
                    Platform.select({native: true})
                  ) {
                    // from sms on chrome ios has inputType insertText
                    // also check that data and text match and have 6 chars
                    // to be fairly certain its from sms
                    if (
                      (e.nativeEvent.data === e.nativeEvent.text &&
                        e.nativeEvent.data.length === 6) ||
                      // native
                      (Platform.select({native: true}) && e.nativeEvent.text.length === 6)
                    ) {
                      dispatch({
                        type: 'insertFromPaste',
                        payload: {value: e.nativeEvent.text},
                      });

                      // move focus to the last input and then blur
                      otpInput.current[5].focus();
                      otpInput.current[5].blur();
                    } else {
                      // this is when entering numbers normally web or native
                      dispatch({
                        type: 'insertText',
                        payload: {
                          index: i,
                          value: e.nativeEvent.data || e.nativeEvent.text,
                        },
                      });
                      if (i < numInputs - 1) {
                        otpInput.current[i + 1].focus();
                      }
                    }
                  } else if (e.nativeEvent.inputType === 'insertFromPaste') {
                    dispatch({
                      type: 'insertFromPaste',
                      payload: {value: e.nativeEvent.text},
                    });
                  }
                  // Paste code from SMS ios safari
                  // code from sms is sent as several
                  // onChange event nativeEvent.type = 'input'
                  else if (e.nativeEvent.type === 'input') {
                    dispatch({
                      type: 'sms',
                      payload: {value: e.nativeEvent.text},
                    });
                  }
                }}
                onKeyPress={e => {
                  if (e.nativeEvent.key === 'Backspace') {
                    if (val.length === 0) {
                      if (i > 0) {
                        e.preventDefault();
                        otpInput.current[i - 1].focus();
                      }
                    }
                  } else if (e.nativeEvent.key === 'Enter') {
                    onEnter?.();
                  }
                }}
                style={[styles.textInputStyle, error && styles.textInputStyleError]}
              />
            </View>
            {boxSize ? null : <Text style={styles.errorLineSpacer}> </Text>}
          </View>
        );
      })}
    </View>
  );
}
